import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';
import { ModalRatingAndNumReviews } from '../ModalRatingAndNumReviews';

export const HydroPlusCleanserAndMoisturizerModalContent = ({
  modalCard,
}: {
  modalCard: Card;
}) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        {modal.title}
      </Typography>
      <ModalPricingVolumeAndProductOptionSelector modalCard={modalCard} />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <ModalRatingAndNumReviews modalCard={modalCard} />
      <Typography className="cur-mt-4" size="h3">
        Why it&apos;s special
      </Typography>
      <Typography className="cur-mt-4">
        Skincare is simple with the essential 3-step Curology routine: hydrating
        cleanser, your personalized formula, and intensely hydrating
        moisturizer. These dermatologist-designed essentials deliver clean,
        hydrated skin—without the worry of clogged pores.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>Hydro+ Foaming Gel Cleanser:</strong> Our gentle, nourishing
            face wash is powered by Dual-Layered Hydration Technology to deeply
            hydrate multiple layers of skin and boost your barrier—keeping your
            face feeling soft and clean without stripping.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>Hydro+ Gel-Cream Moisturizer:</strong> Powered with our
            Dual-Layered Hydration Technology, this fast-absorbing gel-cream is
            dermatologist-designed to intensely hydrate skin—locking in a soft,
            smooth-looking glow.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        Use morning and night as go-to essentials in your daily skincare
        routine.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>Hydro+ Foaming Gel Cleanser:</strong> Gently massage onto
            damp face, then rinse off.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>Hydro+ Gel-Cream Moisturizer:</strong> Apply to face and
            neck after cleansing.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        Featured ingredients
      </Typography>
      <Typography className="cur-mt-4" weight="bold">
        Hydro+ Foaming Gel Cleanser
      </Typography>
      <IngredientsAccordion
        body="Deeply hydrates and protects the skin with dual-layered hydration"
        title="Hyaluronic Acid-5 + Polyglutamic Acid Complex"
      />
      <IngredientsAccordion
        body="Locks water in the skin’s outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <IngredientsAccordion
        body="Hydrates and soothes skin"
        title="Aloe vera"
      />
      <IngredientsAccordion
        body="Helps remove impurities and makeup while nourishing the skin"
        title="Squalane"
      />
      <IngredientsAccordion
        body="Helps visibly fade dark spots and soothes skin"
        title="Licorice root extract"
      />
      <IngredientsAccordion
        body="Helps skin’s upper layers draw in and retain moisture"
        title="Sodium PCA"
      />
      <Typography className="cur-mt-4" weight="bold">
        Hydro+ Gel-Cream Moisturizer
      </Typography>
      <IngredientsAccordion
        body="Deeply hydrates and protects the skin with dual-layered hydration"
        title="Hyaluronic Acid-5 + Polyglutamic Acid Complex"
      />
      <IngredientsAccordion
        body="Locks water in the skin's outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <IngredientsAccordion
        body="Helps reduce moisture loss, soothe skin, and support a healthy barrier"
        title="Ectoin"
      />
      <IngredientsAccordion
        body="Fights free radicals, helps visibly fade dark spots, and improves moisture barrier function"
        title="Niacinamide"
      />
      <IngredientsAccordion
        body="Smooth the look of fine lines and wrinkles"
        title="Peptides"
      />
      <IngredientsAccordion
        body="Attracts and retains water while helping to boost elasticity"
        title="Panthenol"
      />
      <Typography className="cur-mt-4" size="h3">
        Full ingredients list
      </Typography>
      <Typography className="cur-mt-4">
        <strong>Hydro+ Foaming Gel Cleanser:</strong> Water, Glycerin, Potassium
        Cocoyl Glycinate, Coco-Betaine, Propanediol, Potassium Cocoate,
        Hyaluronic Acid, Sodium Hyaluronate, Hydrolyzed Sodium Hyaluronate,
        Sodium Acetylated Hyaluronate, Sodium Hyaluronate Crosspolymer,
        Polyglutamic Acid, Caprylhydroxamic Acid, 1,2-Hexanediol, Squalane, Aloe
        Barbadensis Leaf Extract, Glycyrrhiza Glabra (Licorice) Root Extract,
        Coco-Glucoside Glyceryl Oleate, Acrylates/C10-30 Alkyl Acrylate
        Crosspolymer, Sodium Hydroxide, Sodium PCA, Sodium Phytate,
        Ethylhexylglycerin, Citric Acid, Pentylene Glycol
      </Typography>
      <Typography className="cur-mt-4">
        <strong>Hydro+ Gel-Cream Moisturizer:</strong> Water, Butylene Glycol,
        Dimethicone, Glycerin, Trisiloxane, Niacinamide, Trehalose, Polyglutamic
        Acid, Hyaluronic Acid, Sodium Hyaluronate, Hydrolyzed Hyaluronic Acid,
        Hydrolyzed Sodium Hyaluronate, Sodium Acetylated Hyaluronate, Sodium
        Hyaluronate Crosspolymer, Palmitoyl Tripeptide-1, Palmitoyl
        Tetrapeptide-7, Ectoin, Acrylates/C10-30 Alkyl Acrylate Crosspolymer,
        Carbomer, Ethylhexyl Palmitate, Hydrogenated Polydecene, Hydrogenated
        Polyisobutene, Hydroxyacetophenone, Panthenol, Pentylene Glycol,
        Polyacrylate-13, Sodium Polyacryloyldimethyl Taurate, Sorbitan
        Isostearate, Polyglyceryl-10 Laurate, Polysorbate 20, Propanediol,
        Caprylyl Glycol, Ethylhexylglycerin
      </Typography>
    </>
  );
};
